import { useEffect, useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { useAuth } from "@/contexts/AuthContext"
import { getCategories } from "@/api/categoryApi"
// 💡 기획전 상품 유무 확인을 위해 API 추가
import { getPublicProducts } from "@/api/productApi"

export default function Header() {
    const navigate = useNavigate()
    const { user, isLoggedIn, logout } = useAuth()
    const [searchTerm, setSearchTerm] = useState("")

    const [categories, setCategories] = useState<any[]>([])
    const [promoCategory, setPromoCategory] = useState<any | null>(null)
    const [hasPromoProducts, setHasPromoProducts] = useState(false)

    useEffect(() => {
        const fetchCategoriesAndPromo = async () => {
            try {
                // 1. 전체 카테고리 목록 불러오기
                const response = await getCategories()
                const allCategories = response?.data?.items || []

                // 2. '기획전' 카테고리 분리 (이름이 '기획전'이거나 코드가 'PROMO/EVENT'인 것)
                const promoCat = allCategories.find((cat: any) =>
                  cat.name === "기획전" || cat.categoryCode === "PROMO" || cat.categoryCode === "EVENT"
                )

                // 3. 기획전을 제외한 '일반 카테고리'만 상태에 저장
                const normalCategories = allCategories.filter((cat: any) =>
                  cat.categoryId !== promoCat?.categoryId
                )
                setCategories(normalCategories)

                // 4. 기획전 카테고리가 존재한다면, 실제 등록된 상품이 있는지 1개만 조회해보기
                if (promoCat) {
                    const prodRes = await getPublicProducts({
                        categoryId: promoCat.categoryId,
                        size: 1 // 💡 전체를 다 불러올 필요 없이 1개만 조회해서 체크
                    })
                    const promoItems = prodRes?.data?.items || prodRes?.data?.data?.items || prodRes?.data || []

                    if (promoItems.length > 0) {
                        setPromoCategory(promoCat)
                        setHasPromoProducts(true) // 상품이 있을 때만 true
                    }
                }
            } catch (error) {
                console.error("카테고리 데이터를 불러오지 못했습니다.", error)
            }
        }
        fetchCategoriesAndPromo()
    }, [])

    const handleSearch = (e?: React.FormEvent) => {
        if (e) e.preventDefault()
        if (!searchTerm.trim()) return
        navigate(`/products?keyword=${encodeURIComponent(searchTerm.trim())}`)
    }

    return (
      <header className="w-full bg-white border-b border-slate-200">
          {/* 1단: 최상단 유틸리티 메뉴 (얇은 바) */}
          <div className="bg-slate-50 border-b border-slate-100">
              <div className="mx-auto flex h-8 max-w-7xl items-center justify-end gap-3 px-4 text-[11px] font-medium text-slate-500 lg:px-6">
                  {isLoggedIn ? (
                    <>
                        <span className="text-slate-800">{user?.memberName}님</span>
                        <button onClick={logout} className="hover:text-slate-800">로그아웃</button>
                        <button onClick={() => navigate("/mypage")} className="hover:text-slate-800">마이페이지</button>
                    </>
                  ) : (
                    <>
                        <button onClick={() => navigate("/guest/order")} className="hover:text-slate-800 font-bold">비회원 주문 조회</button>
                        <button onClick={() => navigate("/login")} className="hover:text-slate-800">로그인</button>
                        <button onClick={() => navigate("/signup")} className="hover:text-slate-800">회원가입</button>
                    </>
                  )}
                  <button onClick={() => navigate("/cs")} className="hover:text-slate-800">고객센터</button>
              </div>
          </div>

          {/* 2단: 메인 헤더 (로고, 검색창, 아이콘 메뉴) */}
          <div className="mx-auto flex h-24 max-w-7xl items-center justify-between px-4 lg:px-6">
              {/* 로고 */}
              <div className="flex cursor-pointer items-center gap-2" onClick={() => navigate("/")}>
                  <h1 className="text-3xl font-black tracking-tighter text-[#00A1E9]">MG퀵플라워</h1>
              </div>

              {/* 중앙 검색창 */}
              <div className="hidden flex-1 px-12 lg:block">
                  <form onSubmit={handleSearch} className="relative mx-auto w-full max-w-[400px]">
                      <input
                        type="text"
                        placeholder="찾으시는 꽃이나 식물이 있나요?"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="h-11 w-full rounded-full border-2 border-rose-500 bg-white pl-5 pr-12 text-sm font-medium outline-none focus:ring-0"
                      />
                      <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-500">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                          </svg>
                      </button>
                  </form>
              </div>

              {/* 우측 장바구니 */}
              <div className="flex items-center gap-4">
                  <button onClick={() => navigate("/cart")} className="flex flex-col items-center gap-1 text-slate-700 hover:text-rose-600">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>
                      <span className="text-[11px] font-bold">장바구니</span>
                  </button>
              </div>
          </div>

          {/* 3단: GNB (글로벌 네비게이션) */}
          <nav className="border-t border-slate-100">
              <div className="mx-auto flex max-w-7xl items-center gap-8 px-4 py-3 text-[15px] font-bold text-slate-800 lg:px-6 overflow-x-auto whitespace-nowrap">
                  <Link to="/categories" className="flex items-center gap-1 hover:text-rose-600">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>
                      전체카테고리
                  </Link>
                  <span className="w-px h-3 bg-slate-300 mx-1"></span>

                  {/* 일반 카테고리 렌더링 */}
                  {categories.map((cat) => (
                    <Link key={cat.categoryId} to={`/products?categoryId=${cat.categoryId}`} className="hover:text-rose-600">
                        {cat.name}
                    </Link>
                  ))}

                  {/* 💡 기획전 렌더링 (상품이 있을 경우에만 맨 끝에 구분선과 함께 추가됨) */}
                  {hasPromoProducts && promoCategory && (
                    <>
                        <span className="w-px h-3 bg-slate-300 mx-1"></span>
                        <Link to={`/products?categoryId=${promoCategory.categoryId}`} className="hover:text-rose-600">
                            {promoCategory.name}
                        </Link>
                    </>
                  )}

              </div>
          </nav>
      </header>
    )
}